/* ============================================================
   DMC Wholesale — Bulk Orders: fast isolated renderer + naming/order
   Loads AFTER dmc-bulk-orders.js and replaces its render/export helpers.

   Why: html2canvas clones the ENTIRE page (all theme DOM + all theme CSS)
   for every single capture. On a heavy theme page that costs seconds per
   card, and the Weekly Ads run makes hundreds of captures.
   Here every card is drawn inside a tiny, hidden iframe that only holds
   the card's own CSS, so each capture clones a few dozen nodes instead of
   the whole page. A small pool of these iframes is reused for the run.
   ============================================================ */

var FRAME_H = 2600;
var _frames = [], _frameCount = 0, _frameWaiters = [];
var _perf = { n: 0, ms: 0 };

function frameCss() {
  var ff = 'Arial,Helvetica,sans-serif';
  try {
    var b = (getComputedStyle(document.body).fontFamily || '').replace(/[<>{}]/g, '');
    if (b) ff = b + ',Arial,Helvetica,sans-serif';
  } catch (e) {}
  return 'html,body{margin:0;padding:0;background:#fff}' +
    'body{font-family:' + ff + '}*{box-sizing:border-box}' +
    '.popup-save-clone{width:800px;background:#fff;display:flex;flex-direction:column;padding:0;margin:0}' +
    '.clone-inner{display:flex;flex-direction:column;align-items:center;padding:0 20px 20px;width:100%}' +
    '.clone-inner h2{font-size:42px;font-weight:900;margin:0 0 10px;padding-top:44px;text-align:center;line-height:1.15;word-break:break-word;color:#000;width:100%}' +
    '.images{display:flex;justify-content:center;margin-bottom:10px;width:100%}' +
    '.images img{width:100%;max-width:580px;height:auto;border-radius:6px;object-fit:contain;border:none;display:block}' +
    '.thumbnails{display:flex;justify-content:center;gap:8px;margin-bottom:10px;flex-wrap:wrap;width:100%}' +
    '.thumbnails img{width:100px;height:75px;object-fit:cover;border:1px solid #ccc;border-radius:4px}' +
    '.popup-price{font-size:38px;font-weight:900;color:#e00;margin:6px 0 0;text-align:center;letter-spacing:-0.5px}' +
    '.popup-compare{font-size:17px;color:#666;text-decoration:line-through;margin:-2px 0 8px;text-align:center}' +
    '.clone-inner .description{text-align:center;font-size:17px;line-height:1.55;color:#333;padding:4px 10px;margin-bottom:16px;width:100%}';
}

function makeFrame() {
  return new Promise(function (res) {
    var f = document.createElement('iframe');
    f.setAttribute('aria-hidden', 'true');
    f.style.cssText = 'position:fixed;left:-10000px;top:0;width:800px;height:' + FRAME_H + 'px;border:0;pointer-events:none;';
    f.onload = function () { res({ el: f, busy: true }); };
    f.srcdoc = '<!doctype html><html><head><meta charset="utf-8"><style>' + frameCss() + '</style></head><body></body></html>';
    document.body.appendChild(f);
  });
}
async function takeFrame() {
  var idle = _frames.find(function (f) { return !f.busy; });
  if (idle) { idle.busy = true; return idle; }
  if (_frameCount < RENDER_CONCURRENCY) {
    _frameCount++;
    var f = await makeFrame();
    _frames.push(f);
    return f;
  }
  return new Promise(function (res) { _frameWaiters.push(res); });
}
function releaseFrame(f) {
  var w = _frameWaiters.shift();
  if (w) w(f); else f.busy = false;
}
function destroyFrames() {
  _frames.forEach(function (f) { try { f.el.remove(); } catch (e) {} });
  _frames = []; _frameCount = 0; _frameWaiters = [];
}

async function renderInFrame(html, quality) {
  var f = await takeFrame();
  var t0 = performance.now();
  try {
    var doc = f.el.contentDocument;
    doc.body.innerHTML = html;
    var node = doc.body.firstElementChild;
    await waitForImages(node, 8000);
    var canvas = await html2canvas(node, {
      backgroundColor: '#ffffff', scale: EXPORT_SCALE, useCORS: true, allowTaint: false,
      imageTimeout: 10000, logging: false, scrollX: 0, scrollY: 0
    });
    var url = canvas.toDataURL('image/jpeg', quality);
    releaseCanvas(canvas);
    doc.body.innerHTML = '';
    _perf.n++; _perf.ms += performance.now() - t0;
    return url;
  } finally { releaseFrame(f); }
}

/* replaces the old full-page capture helpers */
function renderCardToJpeg(html, w, h, quality) {
  var wrap = (w || h)
    ? '<div style="' + (w ? 'width:' + w + 'px;' : '') + (h ? 'height:' + h + 'px;' : '') + '">' + html + '</div>'
    : html;
  return renderInFrame(wrap, quality);
}

function buildPopupCloneHTML(data) {
  var imgs = data.allImages, imagesHtml = '', thumbsHtml = '';
  if (imgs.length === 1) {
    imagesHtml = '<div class="images single"><img crossorigin="anonymous" src="' + esc(sizedUrl(imgs[0], IMG_MAIN_W)) + '" alt="Product"></div>';
  } else if (imgs.length > 1) {
    imagesHtml = '<div class="images"><img crossorigin="anonymous" src="' + esc(sizedUrl(imgs[0], IMG_MAIN_W)) + '" alt="Product"></div>';
    var extras = extraThumbs(imgs, 5);
    if (extras.length) thumbsHtml = '<div class="thumbnails">' + extras.map(function (u) { return '<img crossorigin="anonymous" src="' + esc(sizedUrl(u, IMG_THUMB_W)) + '" alt="Thumb">'; }).join('') + '</div>';
  }
  return '<div class="popup-save-clone"><div class="clone-inner"><h2>' + esc(data.title) + '</h2>' + imagesHtml + thumbsHtml + data.priceHtml + data.compareHtml +
    '<div class="description">' + esc(truncateDesc(data.rawDesc, 300)) + '</div></div></div>';
}
async function renderPopupToJpeg(data, quality) {
  try { return await renderInFrame(buildPopupCloneHTML(data), quality); }
  finally { if (!bulkOpRunning) destroyFrames(); } /* single "Save as Image" clicks clean up after themselves */
}

/* frames are released when a run ends; the summary shows the average render time */
function endBulkOp() {
  bulkOpRunning = false;
  setExportButtonsDisabled(false);
  _prefetchToken++;
  imgCache.clear();
  destroyFrames();
  _perf = { n: 0, ms: 0 };
}
function finishProgress(summaryText) {
  if (_perf.n) summaryText += '\n⏱ ' + _perf.n + ' renders, avg ' + (_perf.ms / _perf.n / 1000).toFixed(2) + 's each';
  var ov = _el('bulk-progress-overlay');
  ov.classList.add('is-done');
  _el('bulk-progress-title').textContent = '✅ Done in ' + fmtDur(Date.now() - _progT0);
  _el('bulk-progress-bar').style.width = '100%';
  _el('bulk-progress-text').textContent = summaryText;
  _el('bulk-progress-meta').textContent = '';
  _el('bulk-cancel-btn').style.display = 'none';
  _el('bulk-close-btn').style.display = '';
}

/* ============================================================
   NAMING + ORDER
   - Catalogue files are named from the pricing option.
   - Export order follows the table as it is currently sorted on screen.
   ============================================================ */
function catalogueLabel() {
  var m = String(pricingMode);
  var s = m === 'none' ? 'without pricing' : m === '25' ? 'with pricing' : m === '50' ? 'with EP pricing' : 'with ' + m + '% pricing';
  return '000 Catalogue ' + s;
}
function padNum(n, width) { return String(n).padStart(width, '0'); }

/* selected ids, in the order the rows currently appear in the table */
function getSelectedIds() {
  var raw;
  try { raw = JSON.parse(localStorage.getItem('selectedProducts') || '[]'); } catch (e) { raw = []; }
  var left = new Set(raw), ordered = [];
  document.querySelectorAll('.product-checkbox').forEach(function (cb) {
    if (left.has(cb.value)) { ordered.push(cb.value); left.delete(cb.value); }
  });
  left.forEach(function (v) { ordered.push(v); });
  return ordered;
}

function savePdfDoc(doc, baseName) {
  var fn = (baseName === 'Selected_Products_4up' ? catalogueLabel() + ' - 4 per sheet' : catalogueLabel()) + '.pdf';
  try { doc.save(fn); } catch (e) { downloadBlob(doc.output('blob'), fn); }
  return fn;
}

function createImageSink(JSZipLib, zipBaseName) {
  var zip = JSZipLib ? new JSZipLib() : null, count = 0;
  return {
    usesZip: !!zip,
    count: function () { return count; },
    add: async function (folder, name, dataUrl) {
      count++;
      if (zip) zip.folder(folder).file(name, dataUrl.slice(dataUrl.indexOf(',') + 1), { base64: true });
      else { downloadDataUrl(dataUrl, name); await delay(150); }
    },
    finish: async function () {
      if (!zip || !count) return null;
      var blob = await zip.generateAsync({ type: 'blob', compression: 'STORE' }, function (m) { setProgressLabel('Building ZIP… ' + Math.round(m.percent) + '%'); });
      var fn = zipBaseName + '.zip';
      downloadBlob(blob, fn);
      return fn;
    }
  };
}

/* Same engine as before; only the file names changed and images are numbered in sorted order. */
async function runExports(o) {
  if (bulkOpRunning) { alert('An export is already running — please wait for it to finish.'); return 'busy'; }
  var ids = getSelectedIds();
  if (!ids.length) { alert('No products selected.'); return 'no-selection'; }
  var needsPages = o.pdfSingle || o.pdfQuad || o.quadImages;
  if (needsPages && ids.length > 150) { alert('Max 150 products at a time.'); return 'too-many'; }
  if ((o.pdfSingle || o.pdfQuad) && !(window.jspdf && window.jspdf.jsPDF)) { alert('PDF library is not loaded on this page.'); return 'no-lib'; }
  if (typeof html2canvas === 'undefined') { alert('Image library (html2canvas) is not loaded on this page.'); return 'no-lib'; }
  if (o.weekly && !confirm('Weekly Ads will create "' + catalogueLabel() + '" (PDF, PDF 4 per sheet, and the images) for ' + ids.length + ' selected product(s). Continue?')) return 'cancelled';
  if (!o.weekly && o.popupImages && !needsPages && ids.length > 40 && !confirm('More than 40 products selected. Continue?')) return 'cancelled';
  if (!beginBulkOp()) return 'busy';

  var qEl = document.getElementById('export-quality');
  EXPORT_SCALE = (qEl && parseFloat(qEl.value)) || 1.6;
  var zipEl = document.getElementById('export-zip');
  var wantsImages = o.quadImages || o.popupImages;
  var useZip = wantsImages && (!zipEl || zipEl.checked);
  var label = catalogueLabel();
  var failures = 0, summary = [];

  showProgress(o.weekly ? '📅 Weekly Ads' : '⚙️ Exporting');
  try {
    setProgressLabel('Fetching product data…');
    var zipPromise = useZip ? loadJsZip() : Promise.resolve(null);
    var all = await fetchProductsByIds(ids.map(function (id) { return 'gid://shopify/Product/' + id; }));
    var products = all.filter(function (p) { return p && p.variants && p.variants.edges && p.variants.edges[0] && p.variants.edges[0].node.availableForSale; });
    if (!products.length) { hideProgress(); alert('No available products to export.'); return 'no-available'; }

    var quadChunks = chunk(products, 4);
    var doQuad = o.pdfQuad || o.quadImages;
    if (o.quadImages && !o.pdfSingle && !o.weekly && quadChunks.length > 30 && !confirm('This will save ' + quadChunks.length + ' page images. Continue?')) { hideProgress(); return 'cancelled'; }

    setProgressPlan((o.pdfSingle ? products.length : 0) + (doQuad ? quadChunks.length : 0) + (o.popupImages ? products.length : 0));

    var urls = [];
    function addThumbs(p) { extraThumbs(allImagesOf(p), 5).forEach(function (u) { urls.push(sizedUrl(u, IMG_THUMB_W)); }); }
    if (o.pdfSingle || o.popupImages) products.forEach(function (p) { var a = allImagesOf(p); if (a[0]) urls.push(sizedUrl(a[0], IMG_MAIN_W)); addThumbs(p); });
    if (doQuad) products.forEach(function (p) { var a = allImagesOf(p); if (a[0]) urls.push(sizedUrl(a[0], IMG_QUAD_W)); addThumbs(p); });
    prefetchImagesInBackground(urls);

    var JSZipLib = await zipPromise;
    var sink = wantsImages ? createImageSink(JSZipLib, label + ' - Images') : null;
    var stages = [o.pdfSingle, doQuad, o.popupImages].filter(Boolean).length, stageNo = 0;
    function stageLabel(t) { return (o.weekly || stages > 1 ? 'Step ' + stageNo + '/' + stages + ' — ' : '') + t; }
    var jsPDF = window.jspdf && window.jspdf.jsPDF;

    if (o.pdfSingle) {
      stageNo++; setProgressLabel(stageLabel('Product pages (PDF)'));
      var pages = await runPool(products, RENDER_CONCURRENCY, async function (p) {
        try { return await renderCardToJpeg(buildProductCardHTML(p, SINGLE_PAGE_OPTS), PDF_PAGE_W, PDF_PAGE_H, Q_SINGLE); }
        catch (e) { failures++; console.warn('Page failed:', p.title, e); return null; }
        finally { tickProgress(); }
      });
      if (bulkCancel) { hideProgress(); return 'cancelled'; }
      var doc = new jsPDF(), pw = doc.internal.pageSize.getWidth(), ph = doc.internal.pageSize.getHeight(), n = 0;
      pages.forEach(function (u) { if (!u) return; if (n++ > 0) doc.addPage(); doc.addImage(u, 'JPEG', 0, 0, pw, ph, undefined, 'FAST'); });
      if (n) { var f1 = savePdfDoc(doc, 'Selected_Products'); summary.push('📄 ' + f1 + ' — ' + n + ' page(s)'); }
    }

    if (doQuad) {
      stageNo++; setProgressLabel(stageLabel('4-per-page sheets'));
      var sheets = await runPool(quadChunks, RENDER_CONCURRENCY, async function (c) {
        try { return await renderCardToJpeg(buildQuadPageHTML(c), 0, 0, Q_QUAD); }
        catch (e) { failures++; console.warn('Sheet failed', e); return null; }
        finally { tickProgress(); }
      });
      if (bulkCancel) { hideProgress(); return 'cancelled'; }
      if (o.pdfQuad) {
        var qd = new jsPDF(), qw = qd.internal.pageSize.getWidth(), qh = qd.internal.pageSize.getHeight(), qn = 0;
        sheets.forEach(function (u) { if (!u) return; if (qn++ > 0) qd.addPage(); qd.addImage(u, 'JPEG', 0, 0, qw, qh, undefined, 'FAST'); });
        if (qn) { var f2 = savePdfDoc(qd, 'Selected_Products_4up'); summary.push('📄 ' + f2 + ' — ' + qn + ' sheet(s)'); }
      }
      if (o.quadImages) {
        var sw = String(sheets.length).length;
        for (var si = 0; si < sheets.length; si++) if (sheets[si]) await sink.add('4 per sheet', label + ' - 4 per sheet - ' + padNum(si + 1, sw) + '.jpg', sheets[si]);
      }
    }

    if (o.popupImages) {
      stageNo++; setProgressLabel(stageLabel('Product images'));
      var pwid = String(products.length).length;
      await runPool(products, RENDER_CONCURRENCY, async function (p, idx) {
        try {
          var u = await renderPopupToJpeg(buildPopupData(p), Q_POPUP);
          await sink.add('Product Images', padNum(idx + 1, pwid) + '.' + safeFileName(p.title) + '.jpg', u);
        } catch (e) { failures++; console.warn('Image failed:', p.title, e); }
        finally { tickProgress(); }
      });
      if (bulkCancel) { hideProgress(); return 'cancelled'; }
    }

    if (sink && sink.count()) {
      var zipName = await sink.finish();
      summary.push(zipName ? '🖼️ ' + sink.count() + ' image(s) bundled in ' + zipName : '🖼️ ' + sink.count() + ' image(s) downloaded individually');
    }
    if (failures) summary.push('⚠️ ' + failures + ' item(s) failed to render (see browser console)');
    finishProgress(summary.join('\n') || 'Nothing was exported.');
    return 'ok';
  } catch (e) {
    console.error('Export failed:', e);
    hideProgress();
    alert('Export failed: ' + (e && e.message ? e.message : e));
    return 'error';
  } finally {
    endBulkOp();
  }
}
